import { useEffect, useRef, useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router'
import { api } from '../lib/api.ts'
import { friendlyError } from '../lib/friendly-errors.ts'
import { NewArticleModal } from '../components/NewArticleModal.tsx'
import { StatusDot, statusLabel } from '../components/StatusDot.tsx'

interface GridCard {
  slug: string
  title: string
  wordCount: number
  status: 'published' | 'draft' | 'pending_review' | 'staged' | 'deployed' | 'indexed' | 'archived'
  costUsd?: number
  lintScore: number | null
  lastEditedAt: number
  gscClicks7d: number | null
  fileSha?: string
  draftId?: string
  qcCount?: number | null
  qcHigh?: boolean
}

// Finer-grained status bucket, used by the tracker strip + the filter:
//   live     = on the live site (staged/deployed/indexed/published)
//   ready    = QC-clean and reviewed; safe to publish now (green)
//   review   = QC flagged a high-severity issue; DeepSeek auto-fix is working
//              on it (red)
//   checking = not yet QC'd (the QC/backfill cron grades it) or still writing
type CardCategory = 'live' | 'ready' | 'review' | 'checking'
function cardCategory(c: GridCard): CardCategory {
  if (['staged', 'deployed', 'indexed', 'published'].includes(c.status)) return 'live'
  if (c.qcHigh) return 'review'
  if (typeof c.qcCount === 'number') return 'ready'
  return 'checking'
}

// Card border colour. live/ready = green, review = red, checking = none.
function cardTone(c: GridCard): 'green' | 'red' | 'neutral' {
  const cat = cardCategory(c)
  if (cat === 'live' || cat === 'ready') return 'green'
  if (cat === 'review') return 'red'
  return 'neutral'
}
function cardStyle(c: GridCard): { borderLeft?: string; background?: string } {
  const t = cardTone(c)
  if (t === 'green') return { borderLeft: '4px solid var(--ok)', background: 'rgba(22,101,52,0.06)' }
  if (t === 'red') return { borderLeft: '4px solid var(--danger)', background: 'rgba(185,28,28,0.06)' }
  return {}
}

const POLL_MS = 30000

export function ArticleGrid() {
  const { siteId = 'chc' } = useParams()
  const navigate = useNavigate()
  const [cards, setCards] = useState<GridCard[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [errorHint, setErrorHint] = useState<string | null>(null)
  const [filter, setFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | CardCategory>('all')
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const fetchingRef = useRef(false)
  const [opening, setOpening] = useState<string | null>(null)
  const [newOpen, setNewOpen] = useState(false)
  const [indexing, setIndexing] = useState(false)
  const [indexMsg, setIndexMsg] = useState<string | null>(null)
  const [publishing, setPublishing] = useState(false)
  const [publishMsg, setPublishMsg] = useState<string | null>(null)
  const [publishProgress, setPublishProgress] = useState<{ done: number; failed: number; total: number } | null>(null)

  // One fetch path for the initial load, the 30s poll, and the Refresh button.
  // Background refreshes never show the big spinner or clobber a published
  // error banner; only the first load does.
  async function refreshCards(opts: { initial?: boolean } = {}) {
    if (fetchingRef.current) return
    fetchingRef.current = true
    if (opts.initial) setLoading(true)
    try {
      const res = await api<{ cards: GridCard[] }>(`/api/sites/${siteId}/drafts`)
      setCards(res.cards)
      setLastUpdated(new Date())
      setError(null)
      setErrorHint(null)
    } catch (err) {
      if (opts.initial) {
        const f = friendlyError(err as Error)
        setError(f.message)
        setErrorHint(f.hint ?? null)
      }
      // Background refresh failures stay silent — the last good list remains.
    } finally {
      if (opts.initial) setLoading(false)
      fetchingRef.current = false
    }
  }

  // Initial load + reload when the site changes.
  useEffect(() => {
    setCards([])
    setLastUpdated(null)
    refreshCards({ initial: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId])

  // Live tracker: auto-refresh every 30s while the tab is visible, so the
  // "in review" pile visibly shrinks as DeepSeek QC + auto-fix run, and the
  // "Live" count climbs as articles publish. Paused during a publish run (so
  // it doesn't clobber the optimistic card flips) and while the tab is hidden.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.hidden || publishing) return
      refreshCards()
    }, POLL_MS)
    return () => clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, publishing])

  async function archive(slug: string) {
    if (!confirm('Archive this article? It disappears from the list (nothing is deleted forever).')) return
    try {
      await api(`/api/sites/${siteId}/drafts/${slug}/archive`, { method: 'POST' })
      setCards((prev) => prev.filter((c) => c.slug !== slug))
    } catch (e) {
      alert(`Archive failed: ${(e as Error).message}`)
    }
  }

  async function resubmitAll() {
    setIndexing(true)
    setIndexMsg(null)
    try {
      const res = await api<{ submitted: number; succeeded: number; remaining: number }>(
        `/api/sites/${siteId}/drafts/resubmit-all-indexing`,
        { method: 'POST' },
      )
      setIndexMsg(
        `Sent ${res.succeeded}/${res.submitted} to Google + Bing.` +
          (res.remaining > 0 ? ` ${res.remaining} more not yet done — click again to continue.` : ' All caught up.'),
      )
    } catch (err) {
      setIndexMsg(`Send failed: ${friendlyError(err as Error).message}`)
    } finally {
      setIndexing(false)
    }
  }

  // Publish every "ready" article (reviewed + QC-clean) via the existing
  // publish endpoint. Shows a live progress bar, flips each card to live as it
  // lands (so the tracker moves in real time), and reports successes/skips at
  // the end. Anything still "in review" (red) is left behind on purpose.
  async function publishAllClean() {
    const candidates = cards.filter((c) => cardCategory(c) === 'ready')
    if (candidates.length === 0) {
      setPublishMsg('Nothing ready to publish right now. Flagged or not-yet-checked articles are left for review.')
      return
    }
    if (
      !confirm(
        `Publish ${candidates.length} ready article${candidates.length > 1 ? 's' : ''} to the live site now? Anything still in review (red) is left behind.`,
      )
    )
      return
    setPublishing(true)
    setPublishMsg(null)
    setPublishProgress({ done: 0, failed: 0, total: candidates.length })
    let ok = 0
    let failed = 0
    for (const c of candidates) {
      try {
        await api(`/api/sites/${siteId}/drafts/${c.slug}/publish`, {
          method: 'POST',
          body: JSON.stringify({}),
        })
        ok++
        // Optimistic flip to live so the tracker + counts move immediately;
        // the refresh after the loop confirms it from the server.
        setCards((prev) =>
          prev.map((x) => (x.slug === c.slug ? { ...x, status: 'staged' as const } : x)),
        )
      } catch {
        failed++ // lint-blocked or duplicate; left for review
      }
      setPublishProgress({ done: ok, failed, total: candidates.length })
    }
    setPublishMsg(
      `Published ${ok} of ${candidates.length} to the live site.` +
        (failed ? ` ${failed} skipped (lint-blocked or already live) — open those to check.` : ''),
    )
    setPublishing(false)
    await refreshCards()
    setTimeout(() => setPublishProgress(null), 4000)
  }

  async function openAsDraft(slug: string) {
    setOpening(slug)
    try {
      const res = await api<{ id: string; slug: string }>(
        `/api/sites/${siteId}/drafts`,
        { method: 'POST', body: JSON.stringify({ slug }) },
      )
      navigate(`/sites/${siteId}/articles/${res.slug}`)
    } catch (err) {
      alert(`Failed to open: ${(err as Error).message}`)
    } finally {
      setOpening(null)
    }
  }

  const liveCount = cards.filter((c) => cardCategory(c) === 'live').length
  const readyCount = cards.filter((c) => cardCategory(c) === 'ready').length
  const reviewCount = cards.filter((c) => cardCategory(c) === 'review').length
  const checkingCount = cards.filter((c) => cardCategory(c) === 'checking').length

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Articles</h1>
          <div className="subtitle">
            {loading
              ? 'Loading…'
              : `${liveCount} of ${cards.length} live · ${readyCount} ready to publish`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            type="button"
            className="btn btn-primary"
            disabled={publishing || readyCount === 0}
            title="Publish every reviewed article that passed DeepSeek QC (no high-severity issues). Anything still in review stays put."
            onClick={publishAllClean}
          >
            {publishing ? 'Publishing…' : `Publish ${readyCount} ready`}
          </button>
          <button
            type="button"
            className="btn"
            disabled={indexing}
            title="Send every published article's URL to Google + Bing (20 per click)"
            onClick={resubmitAll}
          >
            {indexing ? 'Sending…' : 'Send all to Google'}
          </button>
          <button type="button" className="btn btn-primary" onClick={() => setNewOpen(true)}>
            + New article
          </button>
        </div>
      </div>

      {publishProgress && (
        <div style={{ margin: '0 0 0.75rem' }}>
          <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: 4 }}>
            Publishing {publishProgress.done + publishProgress.failed} of {publishProgress.total}
            {publishProgress.failed ? ` · ${publishProgress.failed} skipped` : ''}
          </div>
          <div style={{ height: 8, borderRadius: 4, background: 'rgba(0,0,0,0.10)', overflow: 'hidden' }}>
            <div
              style={{
                height: '100%',
                width: `${Math.round(((publishProgress.done + publishProgress.failed) / Math.max(1, publishProgress.total)) * 100)}%`,
                background: 'var(--ok)',
                transition: 'width 0.3s ease',
              }}
            />
          </div>
        </div>
      )}
      {publishMsg && <div className="loading" style={{ marginBottom: '0.75rem' }}>{publishMsg}</div>}
      {indexMsg && <div className="loading" style={{ marginBottom: '0.75rem' }}>{indexMsg}</div>}

      {newOpen && <NewArticleModal onClose={() => setNewOpen(false)} />}

      {error && (
        <div className="error-state">
          <strong>{error}</strong>
          {errorHint && <div style={{ marginTop: '0.4rem', fontWeight: 400, color: 'var(--text-muted)' }}>{errorHint}</div>}
        </div>
      )}
      {loading && !error && <div className="loading">Fetching from GitHub…</div>}
      {!loading && !error && cards.length === 0 && <div className="empty">No articles found.</div>}

      {cards.length > 8 ? (
        <input
          type="text"
          placeholder="Find an article by title or address…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ margin: '0 0 12px', maxWidth: 420 }}
        />
      ) : null}

      {cards.length > 0 ? (
        <div style={{ display: 'flex', gap: '0.4rem', margin: '0 0 8px', flexWrap: 'wrap', alignItems: 'center' }}>
          {(
            [
              ['all', `All (${cards.length})`],
              ['live', `🟢 Live (${liveCount})`],
              ['ready', `✅ Ready (${readyCount})`],
              ['review', `🔴 In review (${reviewCount})`],
              ['checking', `⚪ Checking (${checkingCount})`],
            ] as const
          )
            .filter(([v]) => v !== 'checking' || checkingCount > 0)
            .map(([v, label]) => (
              <button
                key={v}
                type="button"
                className={statusFilter === v ? 'btn btn-primary' : 'btn'}
                style={{ fontSize: '0.8rem', padding: '0.3rem 0.7rem' }}
                onClick={() => setStatusFilter(v)}
              >
                {label}
              </button>
            ))}
          <span
            style={{
              marginLeft: 'auto',
              display: 'flex',
              gap: '0.5rem',
              alignItems: 'center',
              fontSize: '0.78rem',
              color: 'var(--text-muted)',
            }}
          >
            {lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString()}` : 'Updating…'}
            <button
              type="button"
              className="btn"
              style={{ fontSize: '0.78rem', padding: '0.25rem 0.6rem' }}
              onClick={() => refreshCards()}
            >
              Refresh
            </button>
          </span>
        </div>
      ) : null}
      {reviewCount + checkingCount > 0 ? (
        <div style={{ margin: '0 0 12px', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
          DeepSeek is auto-checking and fixing the “in review” articles in the background. This list refreshes itself
          every 30 seconds, so red turns to green on its own — no need to reload.
        </div>
      ) : null}

      <div className="card-grid">
        {cards
          .filter((c) => statusFilter === 'all' || cardCategory(c) === statusFilter)
          .filter(
            (c) =>
              !filter.trim() ||
              c.title.toLowerCase().includes(filter.toLowerCase()) ||
              c.slug.toLowerCase().includes(filter.toLowerCase()),
          )
          .map((card) => (
            <div key={card.slug} className="card" style={cardStyle(card)}>
              <Link
                to={`/sites/${siteId}/articles/${card.slug}`}
                className="card-title"
                title={card.title}
              >
                {card.title}
              </Link>
              <div className="card-meta">
                <span title={statusLabel(card.status).hint}>
                  <StatusDot status={card.status} />
                  {statusLabel(card.status).label}
                </span>
                <span>
                  <strong>{card.wordCount.toLocaleString()}</strong> words
                </span>
                {typeof card.qcCount === 'number' && (
                  <span
                    title={
                      card.qcHigh
                        ? 'DeepSeek QC flagged a high-severity issue — auto-fix is working on it'
                        : card.qcCount > 0
                          ? 'DeepSeek QC: only minor (low-severity) notes — safe to publish'
                          : 'DeepSeek QC: clean'
                    }
                    style={{
                      color: card.qcHigh ? 'var(--danger)' : card.qcCount > 0 ? 'var(--warn)' : 'var(--ok)',
                      fontWeight: 600,
                    }}
                  >
                    {card.qcHigh
                      ? `⚑ ${card.qcCount} review`
                      : card.qcCount > 0
                        ? `${card.qcCount} minor`
                        : 'QC ✓'}
                  </span>
                )}
                {card.lintScore !== null && (
                  <span>
                    <strong>{card.lintScore}</strong> lint
                  </span>
                )}
                {typeof card.costUsd === 'number' && card.costUsd > 0 && (
                  <span title="DeepSeek spend on this article">
                    <strong>${card.costUsd.toFixed(2)}</strong> ai
                  </span>
                )}
                <span style={{ color: 'var(--text-faint)' }}>
                  {card.gscClicks7d === null ? '— GSC' : `${card.gscClicks7d} clicks/7d`}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                <Link
                  to={`/sites/${siteId}/articles/${card.slug}`}
                  className="btn"
                  style={{ fontSize: '0.8rem', padding: '0.3rem 0.7rem' }}
                >
                  View
                </Link>
                {card.status === 'published' && (
                  <button
                    type="button"
                    className="btn"
                    style={{ fontSize: '0.8rem', padding: '0.3rem 0.7rem' }}
                    disabled={opening === card.slug}
                    onClick={() => openAsDraft(card.slug)}
                  >
                    {opening === card.slug ? 'Opening…' : 'Open as draft'}
                  </button>
                )}
                {card.status !== 'published' && card.draftId && (
                  <button
                    type="button"
                    className="btn"
                    style={{ fontSize: '0.8rem', padding: '0.3rem 0.7rem', color: 'var(--danger)' }}
                    onClick={() => archive(card.slug)}
                  >
                    Archive
                  </button>
                )}
              </div>
            </div>
          ))}
      </div>
    </>
  )
}
